import {
  FESTIVE_DEFAULT_OFF,
  festivalOn,
  type Festival,
  type FestivalKey,
  joinList,
} from '@shh/shared';
import { Avatar, Button, FestiveScene, type FestiveKind } from '@shh/ui';
import { useEffect, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import type { M } from '../data/derive';
import { useFamily } from '../data/family';
import { locale } from '../lib/locale';
import { todayText } from '../i18n/today';

/**
 * Geburtstage und Festtage (1.13): a birthday in the family or a festive day colours "Heute"
 * (a band with a small picture), the resting wall (a whole drawn scene) and – on the big days –
 * a festive view that pops up once per device. Quiet: no sound; switched off in Einstellungen →
 * Anzeige. No wish list on the birthday itself – real presents are unwrapped (Mike, 04.10.).
 */
export type FestiveDay =
  { kind: 'birthday'; people: M[] } | { kind: 'festival'; festival: Festival };

export function useFestiveDay(): FestiveDay | null {
  const f = useFamily();
  const settings = f.household?.festive ?? { enabled: true, off: FESTIVE_DEFAULT_OFF };
  if (!settings.enabled) return null;
  const people = f.members.filter((m) => m.birthday === f.today.slice(5));
  if (people.length) return { kind: 'birthday', people };
  const festival = festivalOn(f.today, settings.off as FestivalKey[], locale);
  return festival ? { kind: 'festival', festival } : null;
}

export const sceneOf = (day: FestiveDay): FestiveKind =>
  day.kind === 'birthday' ? 'birthday' : day.festival.key;
export const candlesOf = (day: FestiveDay) =>
  day.kind === 'festival' ? (day.festival.candles ?? 4) : 4;

const names = (people: M[]) =>
  joinList(
    people.map((p) => p.name),
    locale,
  );

export function festiveTitle(day: FestiveDay): string {
  return day.kind === 'birthday' ? todayText.birthdayTitle(names(day.people)) : day.festival.text;
}
/** The resting wall's headline. */
export function festiveHeadline(day: FestiveDay): string {
  return day.kind === 'birthday'
    ? todayText.birthdayHeadline(names(day.people))
    : day.festival.text;
}

/** One line on "Heute", under the greeting – with a small picture of the day. */
export function FestiveBand() {
  const day = useFestiveDay();
  if (!day) return null;
  return (
    <section
      aria-label={day.kind === 'birthday' ? todayText.birthday : todayText.festiveDay}
      className="flex items-center gap-gap-md overflow-hidden rounded-card bg-reward-points-surface px-inset-sm py-inset-xs font-semibold"
    >
      <FestiveScene kind={sceneOf(day)} variant="badge" candles={candlesOf(day)} />
      <span className="flex-1">
        {day.kind === 'birthday' ? todayText.birthdayBand(names(day.people)) : day.festival.text}
      </span>
    </section>
  );
}

const SEEN_KEY = 'shh.festive.seen';

/**
 * The festive view on a birthday (and Christmas Eve, New Year, Easter): pops up once per device
 * and day, closes with a tap. The resting wall shows its own scene instead.
 */
export function FestivePopup() {
  const f = useFamily();
  const day = useFestiveDay();
  const id = day ? `${f.today}:${day.kind === 'birthday' ? 'birthday' : day.festival.key}` : '';
  const big = day && (day.kind === 'birthday' || day.festival.big);
  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(SEEN_KEY);
    } catch {
      return id;
    }
  });
  useEffect(() => {
    if (!big || seen === id) return;
    try {
      localStorage.setItem(SEEN_KEY, id);
    } catch {
      /* shows again next time – no harm */
    }
  }, [big, id, seen]);
  if (!day || !big || seen === id) return null;
  const close = () => setSeen(id);
  const person = day.kind === 'birthday' && day.people.length === 1 ? day.people[0] : undefined;

  return (
    // React Aria: Escape, a tap outside and focus all handled like in every other sheet
    <ModalOverlay
      isOpen
      isDismissable
      onOpenChange={(open) => !open && close()}
      className="fixed inset-0 z-50 grid place-items-center bg-surface-scrim p-gutter"
    >
      <Modal className="w-full max-w-narrow">
        <Dialog
          aria-label={festiveTitle(day)}
          className="flex flex-col overflow-hidden rounded-card bg-surface-raised text-center shadow-card outline-none"
        >
          <div className="relative aspect-video w-full">
            <FestiveScene kind={sceneOf(day)} variant="backdrop" candles={candlesOf(day)} />
          </div>
          <div className="flex flex-col items-center gap-gap-md px-inset-lg pb-inset-lg">
            {person && (
              <Avatar
                name={person.name}
                color={person.color}
                emoji={person.emoji}
                size="xl"
                decorative
                className="relative z-10 -mt-12 ring-4 ring-surface-raised"
              />
            )}
            <h2 className="pt-gap-sm font-display text-3xl font-black text-balance">
              {festiveTitle(day)}
            </h2>
            <p className="text-text-secondary">
              {day.kind === 'birthday' ? todayText.celebrate : todayText.niceDays}
            </p>
            <Button className="w-full" onPress={close}>
              {todayText.continue}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
